import { Button } from '@creditornot/cb-button';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { Radio } from '@creditornot/cb-radio';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { IntlShape } from 'react-intl';
import styled from 'styled-components';

import { getMpsDocumentDossier, getMpsDocument, fetchMPSInvoiceDossierInZip } from 'apis/merchant-payout-service/api';
import { VenuePayout } from 'apis/merchant-payout-service/types';
import { APIErrorNotification, WoltModal } from 'common/components';
import { useFetch } from 'common/hooks';
import { downloadFileFromAxiosResponse } from 'common/utils/download';

import { reportVenuePayoutZipDownload } from './behavioral';

const StyledWoltModal = styled(WoltModal)`
  background: ${cssThemedValue('surfaceMain')};
  border-radius: 8px;
  width: 640px;
`;

const StyledContent = styled(ModalContent)`
  display: flex;
`;

type Props = {
  onClose: () => void;
  payoutReport: VenuePayout;
  intl: IntlShape;
};

const StyledRadio = styled(Radio)`
  & + & {
    margin-block-start: 8px;
  }
`;

const intervalTranslations = {
  custom: 'payout-reports.custom',
  hexa_monthly: 'payout-reports.hexa-monthly',
  monthly: 'payout-reports.monthly',
  semi_monthly: 'payout-reports.semi-monthly',
  semi_weekly: 'payout-reports.semi-weekly',
  weekly: 'payout-reports.weekly',
};

const RightContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const RadioWrapper = styled.div``;

const LeftContent = styled.p`
  margin-inline-end: 136px;
`;

export const PayoutMpsReportListModal = ({ intl, onClose, payoutReport }: Props) => {
  const [documentIndex, setDocumentIndex] = useState<number>(0);

  const { data: documents, error } = useFetch(
    () => getMpsDocumentDossier(payoutReport.venue, payoutReport.id),
    [payoutReport.venue, payoutReport.id],
  );

  const t = (id: string) => intl.formatMessage({ id });

  const handleDownloadInvoice = () => {
    if (!documents) {
      return;
    }

    getMpsDocument(payoutReport.venue, payoutReport.id, documents[documentIndex].documentId)
      .then(downloadFileFromAxiosResponse)
      .catch(() => toast('Invoice generation failed.'));
  };

  const handleDownloadZip = () => {
    fetchMPSInvoiceDossierInZip(payoutReport.venue, payoutReport.id)
      .then(response => {
        downloadFileFromAxiosResponse(response);
        reportVenuePayoutZipDownload();
      })
      .catch(() => toast('There was a problem downloading zip file.'));
  };

  return (
    <>
      {error && <APIErrorNotification error={error} />}
      {documents && documents.length && (
        <StyledWoltModal isOpen>
          <ModalHeader style={{ paddingLeft: 16 }} onCloseClick={onClose}>
            {t('payout-reports.report')} {t(intervalTranslations[payoutReport.interval])} {payoutReport.intervalNumber}
          </ModalHeader>

          <StyledContent>
            <LeftContent>Document</LeftContent>
            <RightContent>
              {documents.map((document, index) => (
                <RadioWrapper key={document.type}>
                  <StyledRadio checked={index === documentIndex} value={index} onChange={() => setDocumentIndex(index)}>
                    {document.type
                      .split('_')
                      .map(_ => `${_.charAt(0).toUpperCase()}${_.slice(1).toLowerCase()}`)
                      .join(' ')}
                  </StyledRadio>
                </RadioWrapper>
              ))}
            </RightContent>
          </StyledContent>

          <ModalFooter style={{ justifyContent: 'space-between' }}>
            <Button size="small" variant="blue" onClick={handleDownloadZip}>
              Download all
            </Button>
            <Button size="small" variant="blue" onClick={handleDownloadInvoice}>
              Download
            </Button>
          </ModalFooter>
        </StyledWoltModal>
      )}
    </>
  );
};
